/**
 * The Productgegevens screen, decided outside React.
 *
 * `productSync.js` answers "what would copying this do"; this file answers the questions the
 * screen itself has: which rows are on the table, how many of them still have no warehouse
 * data, what a stored value looks like written out, and what is left of a sync plan after the
 * user unticks one of its lines.
 *
 * Pure on purpose - the table is the first place anyone will ever be able to see how much of
 * the freezer is actually filled in, so the counting has to be testable rather than inlined in
 * a `useMemo`.
 */

import { getSyncableProperty, WAREHOUSE_FIELDS } from "../constants/ProductSyncConstant";
import { buildArticleCatalogue, isPackageProduct, productKey, weightLabel } from "./articleCode";
import { articlePacks, needsPackCheck as packsNeedCheck, normalisePiecesByWeight, normalisePiecesPerPack } from "./packaging";
import { itemWeight, normalizeProductName } from "./pickRoute";
import {
  compactProductName,
  findLinkForProduct,
  linkCounterpart,
  normaliseGrams,
  SHOP_VCE,
} from "./productSync";

/** What an unset value reads as in the melding, so an empty cell is never just blank. */
export const EMPTY_VALUE_LABEL = "(leeg)";

/** The filter values of the shop selector; "all" is not a shop. */
export const SHOP_FILTER_ALL = "all";

/** The warehouse fields as the form wants them: every key present, missing means null. */
export const warehouseValuesOf = (product) =>
  WAREHOUSE_FIELDS.reduce(
    (values, key) => ({
      ...values,
      [key]: !product || product[key] === undefined ? null : product[key],
    }),
    {},
  );

/**
 * Whether this product has the data the freezer needs.
 *
 * Both gram values, and only those: the pick weight is what the packing slip states and what
 * box-size prediction reasons about, the volume weight is what decides which box it goes in.
 * A pick note is advice for the picker - a product without one is not incomplete, and counting
 * it as such would put half the catalogue on the "nog te doen" pile forever.
 */
export const hasWarehouseData = (product) =>
  normaliseGrams(product && product.pickWeightGram) !== null &&
  normaliseGrams(product && product.volumeWeightGram) !== null;

/**
 * Per product (`shop|id`, like the rows), the vakken it lies in on the pick route, in walking
 * order: `{ location, zone, sequence, weight }`. A product split per weight lies in one vak per
 * weight. A spot without a vak code still counts: it has its place on the route, only no label.
 *
 * Read the way the pick rounds read the route (buildPickRouteIndex): only active entries with a
 * position, and on the shop and id of the item as stored, so this column never shows a vak the
 * scanner would not send the picker to.
 */
export const buildVakIndex = (entries) => {
  const index = new Map();
  (entries || [])
    .filter((entry) => !!entry && entry.active !== false && typeof entry.sequence === "number")
    .forEach((entry) => {
      (entry.products || []).forEach((item) => {
        if (!item || !item.productId) return;
        const key = productKey(item.shopSource, item.productId);
        const vakken = index.get(key) || [];
        vakken.push({ location: entry.location || null, zone: entry.zone || null, sequence: entry.sequence, weight: itemWeight(item) });
        index.set(key, vakken);
      });
    });
  index.forEach((vakken) => vakken.sort((a, b) => a.sequence - b.sequence));
  return index;
};

/**
 * Sorting on the vak column follows the walking order - the first vak of each product - which is
 * the popularity the route was laid out on: the products that matter most come first. Products
 * that are not on the route go last in both directions; they are still to be placed, not the
 * least important ones. (antd reverses the result for "descend", hence the sortOrder.)
 */
export const compareRoutePosition = (a, b, sortOrder) => {
  const left = a.routeSequence;
  const right = b.routeSequence;
  if (left === null && right === null) return 0;
  if (left === null) return sortOrder === "descend" ? -1 : 1;
  if (right === null) return sortOrder === "descend" ? 1 : -1;
  return left - right;
};

/**
 * One row per product of both shops, with its link, its vakken and its pieces per package (per
 * article: per weight for a product with weights, see functions/packaging) already resolved.
 *
 * Gram values are normalised here, so the table sorts on numbers even though Firestore holds
 * some of them as strings - "900" must not sort before "1200".
 */
export const buildProductRows = ({ products, links, pickRoute }) => {
  const vakIndex = buildVakIndex(pickRoute);
  const catalogue = buildArticleCatalogue(products);
  return (products || [])
    .filter((product) => !!product && !!product.id)
    .map((product) => {
      const shopSource = product.shopSource || SHOP_VCE;
      const link = findLinkForProduct(links, shopSource, product.id);
      const vakken = vakIndex.get(productKey(shopSource, product.id)) || [];
      const packs = articlePacks(product, (catalogue.get(productKey(shopSource, product.id)) || {}).articles || []);
      return {
        key: `${shopSource}|${product.id}`,
        id: product.id,
        shopSource,
        product,
        name: product.name || "",
        pickWeightGram: normaliseGrams(product.pickWeightGram),
        volumeWeightGram: normaliseGrams(product.volumeWeightGram),
        // Per article; null for a package, whose components are picked
        packs,
        isPackage: isPackageProduct(product),
        needsPackCheck: packsNeedCheck(packs),
        // For sorting: the largest package size of any of its articles, 1 for per piece
        piecesPerPackMax: Math.max(1, ...(packs || []).map((pack) => pack.piecesPerPack || 1)),
        pickNote: product.pickNote || null,
        updatedAt: product.updatedAt || null,
        updatedBy: product.updatedBy || null,
        link: link || null,
        counterpart: !link ? null : linkCounterpart(link, shopSource),
        isLinked: !!link,
        isComplete: hasWarehouseData(product),
        vakken,
        routeSequence: vakken.length > 0 ? vakken[0].sequence : null,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "nl") || a.shopSource.localeCompare(b.shopSource));
};

/** A row whose pieces per package someone should look at, for one of its articles: see packAdvice. */
export const needsPackCheck = (row) => !!row && !!row.needsPackCheck;

/**
 * The filters staff actually need: find a product, look at one shop, and above all see what is
 * still empty - the first round through this screen is a catch-up, not maintenance. Which goes
 * for the pieces per package too: `onlyPackCheck` keeps the products the webshop sells in steps
 * of more than one while the number is not filled in, or does not fit those steps.
 *
 * Searching also matches the linked product's name, so the pair is found under either spelling.
 * Every word typed has to occur somewhere in the name, rather than the whole string in one
 * piece: the two shops write the same product as "Kipnuggets 1 kg" and "Kip nuggets 1kg", and a
 * search that only matches one of those spellings is a search nobody trusts.
 *
 * Which is also why the name is searched with its spaces removed: someone typing "kipnuggets"
 * is looking for both of those products, and would otherwise be told one of them does not
 * exist. The words typed are matched against that same compacted name, so word order and the
 * spacing of either spelling stop mattering.
 */
export const filterProductRows = (rows, { search, shop, onlyMissing, onlyUnlinked, onlyPackCheck } = {}) => {
  const needles = normalizeProductName(search || "")
    .split(" ")
    .filter((word) => !!word);

  return (rows || []).filter((row) => {
    if (!!shop && shop !== SHOP_FILTER_ALL && row.shopSource !== shop) return false;
    if (onlyMissing && row.isComplete) return false;
    if (onlyUnlinked && row.isLinked) return false;
    if (onlyPackCheck && !needsPackCheck(row)) return false;
    if (needles.length === 0) return true;

    // Compacted per name, not over the two of them joined: gluing the names together would
    // invent matches that straddle the boundary between them.
    const haystack = [row.name, row.counterpart && row.counterpart.name]
      .filter((part) => !!part)
      .map(compactProductName)
      .join(" ");
    return needles.every((word) => haystack.includes(word));
  });
};

const emptyShopSummary = () => ({ total: 0, linked: 0, missing: 0 });

/**
 * How many products each shop has, how many are linked and how many still have no warehouse
 * data. Nobody has ever measured this - it is the number that says how far the catch-up is.
 */
export const summariseProductRows = (rows) => {
  const summary = { total: 0, linked: 0, missing: 0, shops: {} };

  (rows || []).forEach((row) => {
    if (!summary.shops[row.shopSource]) summary.shops[row.shopSource] = emptyShopSummary();
    const shop = summary.shops[row.shopSource];

    summary.total += 1;
    shop.total += 1;
    if (row.isLinked) {
      summary.linked += 1;
      shop.linked += 1;
    }
    if (!row.isComplete) {
      summary.missing += 1;
      shop.missing += 1;
    }
  });

  return summary;
};

/** Grams as the warehouse writes them: "2.400 g", or "(leeg)" when there is no value. */
export const formatGrams = (value) => {
  const grams = normaliseGrams(value);
  if (grams === null) return EMPTY_VALUE_LABEL;
  return `${grams.toLocaleString("nl-NL")} g`;
};

/**
 * A stored value written out for the melding, per property type.
 *
 * The prompt is a statement about two concrete products - "Pickgewicht: 2400 g -> 3000 g" - so
 * every value has to render as something a human recognises, including the ones the registry
 * carries for later phases (lists, maps, booleans).
 */
export const formatSyncValue = (key, value) => {
  const property = getSyncableProperty(key);
  const type = (property && property.type) || "text";

  if (type === "grams") return formatGrams(value);
  if (type === "pieces") {
    const pieces = normalisePiecesPerPack(value);
    return pieces === null ? EMPTY_VALUE_LABEL : `${pieces} per verpakking`;
  }
  if (type === "piecesByWeight") {
    const map = normalisePiecesByWeight(value);
    const weights = Object.keys(map);
    return weights.length === 0 ? EMPTY_VALUE_LABEL : weights.map((weight) => `${weightLabel(weight)}: ${map[weight]}`).join(", ");
  }
  if (value === null || value === undefined || value === "") return EMPTY_VALUE_LABEL;
  if (type === "boolean") return value ? "Ja" : "Nee";

  if (type === "list") {
    if (!Array.isArray(value) || value.length === 0) return EMPTY_VALUE_LABEL;
    return value
      .map((item) => (item && typeof item === "object" ? Object.values(item).join(" ") : `${item}`))
      .join(", ");
  }

  if (type === "map") {
    if (typeof value !== "object" || Array.isArray(value)) return `${value}`;
    const entries = Object.keys(value);
    if (entries.length === 0) return EMPTY_VALUE_LABEL;
    return entries.map((entryKey) => `${entryKey}: ${value[entryKey]}`).join(", ");
  }

  return `${value}`;
};

/**
 * The plan that is left after the user unticks lines in the melding.
 *
 * The prompt lists every change, but the user decides per line - "de inhoud wel, de picknotitie
 * niet". Narrowing the plan rather than building a second one keeps `applySyncPlan` seeing
 * exactly the shape it expects, including the `from` values its stale check depends on.
 */
export const narrowSyncPlan = (plan, keys) => {
  if (!plan) return null;

  const wanted = new Set(keys || []);
  const changes = (plan.changes || []).filter((change) => wanted.has(change.key));

  return {
    ...plan,
    changes,
    hasChanges: changes.length > 0,
    values: changes.reduce((values, change) => ({ ...values, [change.key]: change.to }), {}),
  };
};

/**
 * Properties the user just changed that this link is deliberately not copying (price, by
 * default). Worth saying out loud: otherwise "ik heb de prijs aangepast en er gebeurde niets
 * aan de andere kant" looks like a bug instead of a setting.
 */
export const notSelectedProperties = (plan) =>
  ((plan && plan.skipped) || []).filter((entry) => entry.reason === "not-selected");
